import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Plus, CheckCircle, Send, MessageSquare, ExternalLink, Pencil, Trash2, AlertTriangle, Archive } from 'lucide-react';
import { CountdownBadge } from '../components/CountdownBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function extractUrl(text) {
  if (!text) return '';
  const match = text.match(/(https?:\/\/[^\s]+)/);
  return match ? match[1] : text;
}

export default function DesignRequests() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [designs, setDesigns] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [clubName, setClubName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [filesLink, setFilesLink] = useState('');
  const [creating, setCreating] = useState(false);
  const [revisionComment, setRevisionComment] = useState({});
  const [confirmValidateId, setConfirmValidateId] = useState(null);
  const [confirmClubName, setConfirmClubName] = useState('');
  const [duplicateError, setDuplicateError] = useState('');
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingLinkType, setEditingLinkType] = useState('');
  const [editLinkValue, setEditLinkValue] = useState('');
  const [editingRevision, setEditingRevision] = useState(null); // { designId, index }
  const [editRevisionText, setEditRevisionText] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/designs`, { withCredentials: true });
      setDesigns(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!clubName.trim() || !instructions.trim()) { toast.error('Nom du club et consignes requis'); return; }
    setCreating(true);
    try {
      if (editingId) {
        await axios.put(`${API}/api/designs/${editingId}`, { club_name: clubName, instructions, files_link: filesLink }, { withCredentials: true });
        toast.success(t('design_updated'));
      } else {
        await axios.post(`${API}/api/designs`, { club_name: clubName, instructions, files_link: filesLink }, { withCredentials: true });
        toast.success(t('design_requested'));
      }
      setShowForm(false);
      setEditingId(null);
      setClubName(''); setInstructions(''); setFilesLink('');
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Erreur';
      if (e.response?.status === 409) {
        setDuplicateError(detail);
      } else {
        toast.error(detail);
      }
    }
    finally { setCreating(false); }
  };

  const handleEdit = (d) => {
    setEditingId(d._id);
    setClubName(d.club_name || '');
    setInstructions(d.instructions_fr || '');
    setFilesLink(d.files_link || '');
    setShowForm(true);
  };

  const handleDelete = async (designId) => {
    if (!window.confirm(t('confirm_delete_design'))) return;
    try {
      await axios.delete(`${API}/api/designs/${designId}`, { withCredentials: true });
      toast.success(t('design_deleted'));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleRevision = async (designId) => {
    const comment = revisionComment[designId];
    if (!comment?.trim()) { toast.error('Commentaire requis'); return; }
    try {
      await axios.put(`${API}/api/designs/${designId}/revision`, { comment }, { withCredentials: true });
      toast.success(t('revision_sent'));
      setRevisionComment(prev => ({ ...prev, [designId]: '' }));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleValidate = async (designId) => {
    try {
      await axios.put(`${API}/api/designs/${designId}/validate`, {}, { withCredentials: true });
      toast.success(t('design_validated'));
      setConfirmValidateId(null);
      setConfirmClubName('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openValidateConfirm = (d) => {
    setConfirmValidateId(d._id);
    setConfirmClubName(d.club_name);
  };

  const startEditLink = (designId, type, currentValue) => {
    setEditingLinkId(designId);
    setEditingLinkType(type);
    setEditLinkValue(currentValue || '');
  };

  const saveEditLink = async () => {
    if (!editingLinkId) return;
    try {
      const payload = {};
      payload[editingLinkType] = editLinkValue;
      await axios.put(`${API}/api/designs/${editingLinkId}`, payload, { withCredentials: true });
      toast.success(t('design_updated'));
      setEditingLinkId(null);
      setEditingLinkType('');
      setEditLinkValue('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const cancelEditLink = () => {
    setEditingLinkId(null);
    setEditingLinkType('');
    setEditLinkValue('');
  };

  const startEditRevision = (designId, index, currentText) => {
    setEditingRevision({ designId, index });
    setEditRevisionText(currentText);
  };

  const saveEditRevision = async () => {
    if (!editingRevision || !editRevisionText.trim()) return;
    try {
      await axios.put(`${API}/api/designs/${editingRevision.designId}/revision/${editingRevision.index}`, { comment: editRevisionText }, { withCredentials: true });
      toast.success(t('design_updated'));
      setEditingRevision(null);
      setEditRevisionText('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteRevision = async (designId, index) => {
    if (!window.confirm(t('confirm_delete_revision'))) return;
    try {
      await axios.delete(`${API}/api/designs/${designId}/revision/${index}`, { withCredentials: true });
      toast.success(t('revision_deleted'));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const statusBadge = (status) => {
    const colors = {
      'en attente': 'bg-amber-100 text-amber-800',
      'design soumis': 'bg-blue-100 text-blue-800',
      'revision demandée': 'bg-orange-100 text-orange-800',
      'validé': 'bg-emerald-100 text-emerald-800'
    };
    return <Badge className={`${colors[status] || 'bg-slate-100 text-slate-800'} text-xs`}>{status}</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('design_requests_title')}</h1>
            <p className="text-sm text-slate-600 mt-1">{t('design_requests_desc')}</p>
          </div>
          <Button data-testid="new-design-btn" onClick={() => { setEditingId(null); setClubName(''); setInstructions(''); setFilesLink(''); setShowForm(true); }} className="bg-slate-900 hover:bg-slate-800">
            <Plus size={16} className="mr-2" /> {t('new_design_request')}
          </Button>
        </div>

        {/* Create Dialog */}
        <Dialog open={showForm} onOpenChange={(v) => { setShowForm(v); if (!v) setEditingId(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editingId ? t('edit_design_request') : t('new_design_request')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">{t('club_name')}</Label>
                <Input data-testid="design-club-name" value={clubName} onChange={e => setClubName(e.target.value)} placeholder="Ex: FC Thuir" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t('instructions_label')}</Label>
                <textarea data-testid="design-instructions" className="w-full border border-slate-300 rounded-md p-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-slate-400"
                  value={instructions} onChange={e => setInstructions(e.target.value)} placeholder={t('instructions_placeholder')} />
                <p className="text-xs text-slate-400 mt-1">{t('instructions_auto_translate')}</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">{t('files_link_label')}</Label>
                <Input data-testid="design-files-link" value={filesLink} onChange={e => setFilesLink(e.target.value)} placeholder="https://drive.google.com/..." />
              </div>
              <Button data-testid="submit-design-request" onClick={handleCreate} disabled={creating} className="w-full bg-slate-900 hover:bg-slate-800">
                {creating ? '...' : (editingId ? t('save') : t('send_request'))}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Designs List - Active only (non validés) */}
        {(() => {
          const activeDesigns = designs.filter(d => d.status !== 'validé');
          const validatedDesigns = designs.filter(d => d.status === 'validé');

          // Group validated by month
          const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
          const archivedByMonth = {};
          validatedDesigns.forEach(d => {
            const date = new Date(d.validated_at || d.created_at);
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!archivedByMonth[key]) archivedByMonth[key] = { year: date.getFullYear(), month: date.getMonth(), designs: [] };
            archivedByMonth[key].designs.push(d);
          });
          const archiveEntries = Object.entries(archivedByMonth).sort(([a], [b]) => b.localeCompare(a));

          return (
            <>
              {activeDesigns.length === 0 && validatedDesigns.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{t('no_design_requests')}</p>
              ) : (
                <>
                  {activeDesigns.length > 0 && (
                    <div className="space-y-4">
                      {activeDesigns.map(d => (
              <Card key={d._id} className="border-slate-200 shadow-sm">
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">{new Date(d.created_at).toLocaleDateString('fr-FR')}</span>
                      <span className="text-sm font-bold">{d.club_name}</span>
                      {d.commercial_name && <span className="text-xs text-slate-400">({d.commercial_name})</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {['en attente', 'revision demandée'].includes(d.status) && d.deadline && (
                        <CountdownBadge deadline={d.deadline} penaltyVnd={d.penalty_vnd} showPenalty={false} t={t} />
                      )}
                      {statusBadge(d.status)}
                      {isAdmin && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600" onClick={() => handleEdit(d)}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-red-600" onClick={() => handleDelete(d._id)}>
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded p-3">
                    <p className="text-xs font-semibold text-slate-500 mb-1">{t('instructions_label')}</p>
                    <p className="text-sm">{d.instructions_fr}</p>
                  </div>

                  {/* Files link with admin edit */}
                  {editingLinkId === d._id && editingLinkType === 'files_link' ? (
                    <div className="flex items-center gap-2">
                      <Input value={editLinkValue} onChange={e => setEditLinkValue(e.target.value)} placeholder="https://..." className="flex-1 text-sm" />
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={saveEditLink}>{t('save')}</Button>
                      <Button size="sm" variant="outline" onClick={cancelEditLink}>{t('cancel')}</Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {d.files_link && (
                        <a href={extractUrl(d.files_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-colors">
                          <ExternalLink size={13} /> {t('client_files')}
                        </a>
                      )}
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-slate-400 hover:text-blue-600 text-xs" onClick={() => startEditLink(d._id, 'files_link', d.files_link)}>
                          <Pencil size={12} className="mr-1" /> {d.files_link ? '' : t('client_files')}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Revisions */}
                  {d.revisions?.length > 0 && (
                    <div className="border-l-2 border-orange-300 pl-3 space-y-1">
                      {d.revisions.map((r, i) => (
                        <div key={i} className="bg-orange-50 rounded p-2">
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-slate-500">{r.author} - {new Date(r.date).toLocaleDateString('fr-FR')}</p>
                            {isAdmin && (
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-slate-400 hover:text-blue-600" onClick={() => startEditRevision(d._id, i, r.comment_fr)}>
                                  <Pencil size={11} />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-slate-400 hover:text-red-600" onClick={() => deleteRevision(d._id, i)}>
                                  <Trash2 size={11} />
                                </Button>
                              </div>
                            )}
                          </div>
                          {editingRevision?.designId === d._id && editingRevision?.index === i ? (
                            <div className="flex items-center gap-2 mt-1">
                              <Input value={editRevisionText} onChange={e => setEditRevisionText(e.target.value)} className="flex-1 text-sm" />
                              <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-7 text-xs" onClick={saveEditRevision}>{t('save')}</Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingRevision(null)}>{t('cancel')}</Button>
                            </div>
                          ) : (
                            <p className="text-sm">{r.comment_fr}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Design submitted - Commercial can validate or request revision */}
                  {d.status === 'design soumis' && (
                    <div className="space-y-2">
                      {editingLinkId === d._id && editingLinkType === 'design_link' ? (
                        <div className="flex items-center gap-2">
                          <Input value={editLinkValue} onChange={e => setEditLinkValue(e.target.value)} placeholder="https://....png" className="flex-1 text-sm" />
                          <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={saveEditLink}>{t('save')}</Button>
                          <Button size="sm" variant="outline" onClick={cancelEditLink}>{t('cancel')}</Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {d.design_link && (
                            <a href={extractUrl(d.design_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors">
                              <ExternalLink size={13} /> {t('view_design')}
                            </a>
                          )}
                          {isAdmin && (
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-slate-400 hover:text-blue-600 text-xs" onClick={() => startEditLink(d._id, 'design_link', d.design_link)}>
                              <Pencil size={12} />
                            </Button>
                          )}
                        </div>
                      )}
                      <div className="space-y-2">
                        <Input
                          data-testid={`revision-comment-input-${d._id}`}
                          placeholder={t('revision_comment_placeholder')}
                          value={revisionComment[d._id] || ''}
                          onChange={e => setRevisionComment(prev => ({ ...prev, [d._id]: e.target.value }))}
                          className="w-full"
                        />
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" data-testid={`request-revision-btn-${d._id}`} className="gap-1 text-orange-700 border-orange-300 hover:bg-orange-50" onClick={() => handleRevision(d._id)}>
                            <MessageSquare size={14} /> {t('request_revision')}
                          </Button>
                          <Button size="sm" className="gap-1 bg-emerald-700 hover:bg-emerald-800" data-testid={`validate-design-btn-${d._id}`} onClick={() => openValidateConfirm(d)}>
                            <CheckCircle size={14} /> {t('validate_design')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {d.status === 'validé' && (
                    <div className="flex items-center gap-2 flex-wrap text-emerald-700">
                      <CheckCircle size={16} />
                      <span className="text-sm font-medium">{t('design_validated')} - {d.validated_at ? new Date(d.validated_at).toLocaleDateString('fr-FR') : ''}</span>
                      {editingLinkId === d._id && editingLinkType === 'design_link' ? (
                        <div className="flex items-center gap-2">
                          <Input value={editLinkValue} onChange={e => setEditLinkValue(e.target.value)} placeholder="https://....png" className="w-64 text-sm" />
                          <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={saveEditLink}>{t('save')}</Button>
                          <Button size="sm" variant="outline" onClick={cancelEditLink}>{t('cancel')}</Button>
                        </div>
                      ) : (
                        <>
                          {d.design_link && (
                            <a href={extractUrl(d.design_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors ml-2" data-testid={`validated-design-link-${d._id}`}>
                              <ExternalLink size={13} /> {t('view_design')}
                            </a>
                          )}
                          {isAdmin && (
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-slate-400 hover:text-blue-600 text-xs" onClick={() => startEditLink(d._id, 'design_link', d.design_link)}>
                              <Pencil size={12} />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
                    </div>
                  )}

                  {/* ARCHIVES - Designs validés groupés par mois */}
                  {archiveEntries.length > 0 && (
                    <div className="space-y-4" data-testid="commercial-design-archives">
                      <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
                        <Archive size={20} className="text-slate-500" />
                        <h2 className="text-lg font-semibold text-slate-700">{t('designer_archives_title')} ({validatedDesigns.length})</h2>
                      </div>
                      <Accordion type="multiple" className="w-full space-y-2">
                        {archiveEntries.map(([key, { year, month, designs: monthDesigns }]) => (
                          <AccordionItem key={key} value={key} className="border border-slate-200 rounded-lg overflow-hidden">
                            <AccordionTrigger className="hover:no-underline py-3 px-4 bg-slate-50 hover:bg-slate-100">
                              <div className="flex items-center justify-between w-full pr-2">
                                <span className="text-sm font-semibold text-slate-700">{MONTHS_FR[month]} {year}</span>
                                <Badge className="bg-emerald-100 text-emerald-800 text-xs">{monthDesigns.length} design(s)</Badge>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <Accordion type="single" collapsible className="w-full">
                                {monthDesigns.map(ad => (
                                  <AccordionItem key={ad._id} value={ad._id}>
                                    <AccordionTrigger className="hover:no-underline py-2.5 px-4">
                                      <div className="flex items-center gap-3 w-full">
                                        <span className="text-xs text-slate-400 w-20">{new Date(ad.validated_at || ad.created_at).toLocaleDateString('fr-FR')}</span>
                                        <span className="text-sm font-bold text-slate-800 flex-1 text-left">{ad.club_name}</span>
                                        <CheckCircle size={14} className="text-emerald-600" />
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                      <div className="px-4 pb-3 space-y-3">
                                        <div className="bg-slate-50 rounded p-3">
                                          <p className="text-xs font-semibold text-slate-500 mb-1">{t('instructions_label')}</p>
                                          <p className="text-sm">{ad.instructions_fr}</p>
                                        </div>
                                        {ad.files_link && (
                                          <a href={extractUrl(ad.files_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-colors">
                                            <ExternalLink size={13} /> {t('client_files')}
                                          </a>
                                        )}
                                        {ad.design_link && (
                                          <a href={extractUrl(ad.design_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors">
                                            <ExternalLink size={13} /> {t('view_design')}
                                          </a>
                                        )}
                                        {ad.revisions?.length > 0 && (
                                          <div className="border-l-2 border-orange-300 pl-3 space-y-1">
                                            <p className="text-xs font-semibold text-orange-600">{t('revisions')} ({ad.revisions.length})</p>
                                            {ad.revisions.map((r, i) => (
                                              <div key={i} className="bg-orange-50 rounded p-2">
                                                <p className="text-xs text-slate-500">{r.author} - {new Date(r.date).toLocaleDateString('fr-FR')}</p>
                                                <p className="text-sm">{r.comment_fr}</p>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <div className="flex items-center gap-2 text-emerald-700">
                                          <CheckCircle size={14} />
                                          <span className="text-xs font-medium">{t('design_validated')} - {ad.validated_at ? new Date(ad.validated_at).toLocaleDateString('fr-FR') : ''}</span>
                                        </div>
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  )}
                </>
              )}
            </>
          );
        })()}
        {/* Confirmation modale validation définitive */}
        <AlertDialog open={!!confirmValidateId} onOpenChange={(open) => { if (!open) { setConfirmValidateId(null); setConfirmClubName(''); } }}>
          <AlertDialogContent data-testid="validate-confirm-dialog">
            <AlertDialogHeader>
              <div className="flex items-center gap-2 text-amber-700 mb-2">
                <AlertTriangle size={24} />
                <AlertDialogTitle className="text-lg">{t('validate_confirm_title')}</AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className="text-sm text-slate-600 space-y-2">
                  <span className="block font-semibold text-slate-800">{t('validate_confirm_message', { club: confirmClubName })}</span>
                  <span className="block">{t('validate_confirm_warning')}</span>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="validate-cancel-btn">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                data-testid="validate-confirm-btn"
                className="bg-emerald-700 hover:bg-emerald-800"
                onClick={() => handleValidate(confirmValidateId)}
              >
                <CheckCircle size={14} className="mr-1" /> {t('validate_definitive')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Duplicate club error dialog */}
        <AlertDialog open={!!duplicateError} onOpenChange={(open) => { if (!open) setDuplicateError(''); }}>
          <AlertDialogContent data-testid="duplicate-club-dialog" className="border-red-300">
            <AlertDialogHeader>
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <AlertTriangle size={24} />
                <AlertDialogTitle className="text-lg text-red-700">{t('duplicate_club_title')}</AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className="text-sm text-red-600 bg-red-50 rounded p-3 font-medium">
                  {duplicateError}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => setDuplicateError('')}>
                {t('understood')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
